import { Alert, Button, Drawer, Group, Select, Stack, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { EXPORTER_STATUSES, type ExporterDto, type ExporterStatus } from '@mexp/shared';
import { errorMessage, http } from '@/api/client';
import { useCountries } from '@/features/shared/lookups';

interface Values {
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  country: string | null;
  address: string;
  website: string;
  notes: string;
  status: ExporterStatus;
}

const EMPTY: Values = { companyName: '', contactPerson: '', email: '', phone: '', country: null, address: '', website: '', notes: '', status: 'active' };

export function ExporterDrawer({
  opened,
  onClose,
  exporter,
  onSaved,
}: {
  opened: boolean;
  onClose: () => void;
  exporter?: ExporterDto | null;
  onSaved?: (e: ExporterDto) => void;
}) {
  const queryClient = useQueryClient();
  const countries = useCountries();
  const [error, setError] = useState<string | null>(null);
  const editing = !!exporter;
  const form = useForm<Values>({
    initialValues: EMPTY,
    validate: {
      companyName: (v) => (v.trim().length >= 2 ? null : 'Enter the company name'),
      email: (v) => (!v.trim() || /^\S+@\S+\.\S+$/.test(v.trim()) ? null : 'Enter a valid email'),
    },
  });

  useEffect(() => {
    if (!opened) return;
    setError(null);
    form.setValues(
      exporter
        ? {
            companyName: exporter.companyName,
            contactPerson: exporter.contactPerson ?? '',
            email: exporter.email ?? '',
            phone: exporter.phone ?? '',
            country: exporter.country,
            address: exporter.address ?? '',
            website: exporter.website ?? '',
            notes: exporter.notes ?? '',
            status: exporter.status,
          }
        : EMPTY,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, exporter]);

  const save = useMutation({
    mutationFn: (v: Values) => {
      const body = { ...v, country: v.country ?? '' };
      if (editing && exporter) return http.patch<ExporterDto>(`/exporters/${exporter.id}`, body);
      const { status: _status, ...create } = body;
      return http.post<ExporterDto>('/exporters', create);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['exporters'] });
      onSaved?.(saved);
      onClose();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <Drawer opened={opened} onClose={onClose} title={editing ? `Edit ${exporter?.companyName}` : 'New exporter'}>
      <form onSubmit={form.onSubmit((v) => save.mutate(v))} noValidate>
        <Stack gap="md">
          {error && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
              {error}
            </Alert>
          )}
          <TextInput label="Company name" withAsterisk {...form.getInputProps('companyName')} />
          <Group grow>
            <TextInput label="Contact person" {...form.getInputProps('contactPerson')} />
            <TextInput label="Phone" {...form.getInputProps('phone')} />
          </Group>
          <TextInput label="Company email" type="email" {...form.getInputProps('email')} />
          <Select label="Country" searchable clearable data={(countries.data ?? []).map((c) => c.name)} {...form.getInputProps('country')} />
          <Textarea label="Address" autosize minRows={2} {...form.getInputProps('address')} />
          <TextInput label="Website" placeholder="www.example.com" {...form.getInputProps('website')} />
          {editing && (
            <Select
              label="Status"
              data={EXPORTER_STATUSES.map((s) => ({ value: s, label: s[0]!.toUpperCase() + s.slice(1) }))}
              {...form.getInputProps('status')}
            />
          )}
          <Textarea label="Notes" autosize minRows={2} {...form.getInputProps('notes')} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              {editing ? 'Save changes' : 'Create exporter'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
